import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  ArrowRight,
  BadgeCheck,
  Briefcase,
  CalendarDays,
  CheckCircle,
  GraduationCap,
  HandHeart,
  Heart,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Upload,
  User,
  Users,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import PageBanner from "../components/PageBanner";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
};

const inputClass = (hasError) =>
  `w-full rounded-md border bg-white px-4 py-3 text-sm text-[#14201f] outline-none transition placeholder:text-[#8b9996] focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15 ${
    hasError ? "border-red-500" : "border-[#d7e2df]"
  }`;

const iconInputClass = (hasError) =>
  `w-full rounded-md border bg-white px-4 py-3 pl-11 text-sm text-[#14201f] outline-none transition placeholder:text-[#8b9996] focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15 ${
    hasError ? "border-red-500" : "border-[#d7e2df]"
  }`;

const ErrorText = ({ error }) =>
  error ? <p className="mt-1.5 text-sm font-medium text-red-600">{error.message}</p> : null;

const Volunteer = () => {
  const { t } = useTranslation();
  const { siteConfig } = useRegionalExperience();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  const availabilityOptions = [
    { id: "weekday_mornings", label: t("volunteer.form.availability.weekdayMornings") },
    { id: "weekday_afternoons", label: t("volunteer.form.availability.weekdayAfternoons") },
    { id: "weekday_evenings", label: t("volunteer.form.availability.weekdayEvenings") },
    { id: "weekends", label: t("volunteer.form.availability.weekends") },
    { id: "special_events", label: t("volunteer.form.availability.specialEvents") },
  ];

  const skillOptions = [
    { id: "event_coordination", label: t("volunteer.skills.eventCoordination") },
    { id: "content_writing", label: t("volunteer.skills.contentWriting") },
    { id: "social_media", label: t("volunteer.skills.socialMedia") },
    { id: "photography", label: t("volunteer.skills.photography") },
    { id: "web_development", label: t("volunteer.skills.webDevelopment") },
    { id: "translation", label: t("volunteer.skills.translation") },
    { id: "medical", label: t("volunteer.skills.medical") },
    { id: "administrative", label: t("volunteer.skills.administrative") },
    { id: "film_making", label: t("volunteer.skills.filmMaking") },
    { id: "accounts", label: t("volunteer.skills.accounts") },
    { id: "organization_development", label: t("volunteer.skills.organizationDevelopment") },
  ];

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayIso = startOfToday.toISOString().split("T")[0];

  const validationSchema = yup.object().shape({
    fullName: yup
      .string()
      .required(t("volunteer.form.validation.nameRequired"))
      .min(3, t("volunteer.form.validation.nameMinLength")),
    email: yup
      .string()
      .required(t("volunteer.form.validation.emailRequired"))
      .email(t("volunteer.form.validation.emailValid")),
    phone: yup
      .string()
      .required(t("volunteer.form.validation.phoneRequired"))
      .matches(/^[0-9+\-\s()]{7,20}$/, t("volunteer.form.validation.phoneValid")),
    city: yup
      .string()
      .required(t("volunteer.form.validation.cityRequired"))
      .min(2, t("volunteer.form.validation.cityValid")),
    state: yup
      .string()
      .required(t("volunteer.form.validation.stateRequired"))
      .min(2, t("volunteer.form.validation.stateValid")),
    country: yup
      .string()
      .required(t("volunteer.form.validation.countryRequired"))
      .min(2, t("volunteer.form.validation.countryValid")),
    zip: yup
      .string()
      .required(t("volunteer.form.validation.zipRequired"))
      .matches(/^[A-Za-z0-9 -]{4,12}$/, t("volunteer.form.validation.zipValid")),
    dob: yup
      .date()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required(t("volunteer.form.validation.dobRequired"))
      .max(new Date(), t("volunteer.form.validation.dobFuture")),
    age: yup
      .number()
      .typeError(t("volunteer.form.validation.ageType"))
      .required(t("volunteer.form.validation.ageRequired"))
      .min(18, t("volunteer.form.validation.ageMin"))
      .max(100, t("volunteer.form.validation.ageMax")),
    gender: yup.string().required(t("volunteer.form.validation.genderRequired")),
    maritalStatus: yup.string().required(t("volunteer.form.validation.maritalStatusRequired")),
    isDikshit: yup.boolean().required(t("volunteer.form.validation.isDikshitRequired")),
    highestEducation: yup
      .string()
      .required(t("volunteer.form.validation.highestEducationRequired")),
    hoursAvailable: yup
      .object()
      .shape({
        hours: yup
          .number()
          .typeError(t("volunteer.form.validation.hoursAvailableType"))
          .required(t("volunteer.form.validation.hoursAvailableRequired"))
          .min(1, t("volunteer.form.validation.hoursAvailableMin")),
        period: yup
          .string()
          .oneOf(["day", "week", "month"])
          .required(t("volunteer.form.validation.periodRequired")),
      })
      .required(),
    occupationType: yup
      .string()
      .required(t("volunteer.form.validation.occupationTypeRequired")),
    occupation: yup.string().when("occupationType", {
      is: (val) => val && !["unemployed", "retired", "student"].includes(val),
      then: (schema) => schema.required(t("volunteer.form.validation.occupationRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    availability: yup
      .array()
      .min(1, t("volunteer.form.validation.availabilityMin"))
      .required(t("volunteer.form.validation.availabilityRequired")),
    availableFrom: yup
      .date()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required(t("volunteer.form.validation.availableFromRequired"))
      .min(startOfToday, t("volunteer.form.validation.availableFromPast")),
    availableUntil: yup
      .date()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .min(yup.ref("availableFrom"), t("volunteer.form.validation.availableUntilAfter")),
    skills: yup
      .array()
      .min(1, t("volunteer.form.validation.skillsMin"))
      .required(t("volunteer.form.validation.skillsRequired")),
    motivation: yup
      .string()
      .required(t("volunteer.form.validation.motivationRequired"))
      .min(50, t("volunteer.form.validation.motivationMinLength")),
    experience: yup.string(),
    consent: yup
      .boolean()
      .oneOf([true], t("volunteer.form.validation.consentRequired"))
      .required(t("volunteer.form.validation.consentRequired")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      city: "",
      state: "",
      country: "",
      zip: "",
      dob: "",
      age: "",
      gender: "",
      maritalStatus: "",
      isDikshit: false,
      highestEducation: "",
      hoursAvailable: {
        hours: "",
        period: "",
      },
      occupationType: "",
      occupation: "",
      availability: [],
      availableFrom: "",
      availableUntil: "",
      skills: [],
      motivation: "",
      experience: "",
      consent: false,
    },
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("volunteer.toast.fileTooLarge"));
      event.target.value = null;
      return;
    }

    if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
      toast.error(t("volunteer.toast.fileTypeInvalid"));
      event.target.value = null;
      return;
    }

    setProfileFile(file);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setProfilePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setProfilePreview(null);
    }
  };

  const removeFile = () => {
    setProfileFile(null);
    setProfilePreview(null);
    const fileInput = document.getElementById("profile-upload");
    if (fileInput) fileInput.value = null;
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();

      formData.append("fullName", data.fullName);
      formData.append("email", data.email);
      formData.append("phone", data.phone);
      formData.append("city", data.city);
      formData.append("state", data.state);
      formData.append("country", data.country);
      formData.append("zip", data.zip);
      formData.append("dob", new Date(data.dob).toISOString());
      formData.append("age", data.age.toString());
      formData.append("occupationType", data.occupationType);
      if (data.occupation) formData.append("occupation", data.occupation);
      formData.append("gender", data.gender);
      formData.append("maritalStatus", data.maritalStatus);
      formData.append("isDikshit", data.isDikshit.toString());
      formData.append("highestEducation", data.highestEducation);
      formData.append(
        "hoursAvailable",
        JSON.stringify({
          hours: parseInt(data.hoursAvailable.hours, 10),
          period: data.hoursAvailable.period,
        })
      );
      formData.append("availability", JSON.stringify(data.availability));
      formData.append("skills", JSON.stringify(data.skills));
      formData.append("availableFrom", new Date(data.availableFrom).toISOString());
      if (data.availableUntil) {
        formData.append("availableUntil", new Date(data.availableUntil).toISOString());
      }
      formData.append("motivation", data.motivation);
      if (data.experience) formData.append("experience", data.experience);
      formData.append("consent", data.consent.toString());
      if (profileFile) formData.append("profile", profileFile);

      await axios.post(import.meta.env.VITE_APP_VOLUNTEER_API, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(t("volunteer.toast.submitSuccess"));
      setIsSubmitted(true);
      reset();
      removeFile();
    } catch (error) {
      console.error("Volunteer submission failed", error);
      toast.error(
        error.response?.data?.message || t("volunteer.toast.submitError")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const occupationType = watch("occupationType");
  const showOccupation =
    occupationType && !["unemployed", "retired", "student"].includes(occupationType);

  return (
    <main id="volunteerTop" className="min-h-screen overflow-x-hidden bg-[#f7faf9] text-[#14201f]">
      <PageBanner
        {...(siteConfig.volunteerPage?.banner || {})}
        rightSlot={
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {[
              [
                t("volunteer.banner.fieldReady.title"),
                t("volunteer.banner.fieldReady.text"),
              ],
              [
                t("volunteer.banner.skillReady.title"),
                t("volunteer.banner.skillReady.text"),
              ],
              [
                t("volunteer.banner.trustReady.title"),
                t("volunteer.banner.trustReady.text"),
              ],
            ].map(([title, text]) => (
              <div
                key={title}
                className="rounded-lg border border-white/18 bg-white/10 p-5 backdrop-blur"
              >
                <ShieldCheck className="h-6 w-6 text-[#f5c45e]" />
                <h2 className="mt-4 text-lg font-semibold">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-white/76">{text}</p>
              </div>
            ))}
          </div>
        }
      />

      <section id="service-lanes" className="border-b border-[#dfe8e6] bg-white py-10">
        <div className="container mx-auto grid gap-4 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
          {[
            [
              t("volunteer.serviceLanes.healthcare.title"),
              t("volunteer.serviceLanes.healthcare.text"),
            ],
            [
              t("volunteer.serviceLanes.futureSkilling.title"),
              t("volunteer.serviceLanes.futureSkilling.text"),
            ],
            [
              t("volunteer.serviceLanes.reliefSeva.title"),
              t("volunteer.serviceLanes.reliefSeva.text"),
            ],
            [
              t("volunteer.serviceLanes.editorialProof.title"),
              t("volunteer.serviceLanes.editorialProof.text"),
            ],
          ].map(([title, text]) => (
            <div key={title} className="rounded-lg border border-[#dfe8e6] p-5">
              <BadgeCheck className="h-6 w-6 text-[#9d1f1c]" />
              <h2 className="mt-4 font-semibold text-[#14201f]">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-[#52615f]">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="volunteer-form" className="py-16 lg:py-20">
        <div className="container mx-auto grid gap-8 px-4 sm:px-6 lg:grid-cols-[0.42fr_0.58fr] lg:px-8">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#9d1f1c]">
              {t("volunteer.intake.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl font-semibold leading-tight md:text-5xl">
              {t("volunteer.intake.heading")}
            </h2>
            <p className="mt-5 text-base leading-8 text-[#52615f]">
              {t("volunteer.intake.description")}
            </p>

            <div className="mt-8 space-y-4">
              {[
                ["1", t("volunteer.intake.step1")],
                ["2", t("volunteer.intake.step2")],
                ["3", t("volunteer.intake.step3")],
              ].map(([step, text]) => (
                <div
                  key={step}
                  className="flex gap-4 rounded-lg border border-[#dfe8e6] bg-white p-4"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#14201f] text-sm font-bold text-white">
                    {step}
                  </span>
                  <p className="text-sm leading-6 text-[#52615f]">{text}</p>
                </div>
              ))}
            </div>
          </aside>

          <div className="rounded-lg border border-[#dfe8e6] bg-white p-5 shadow-sm sm:p-8">
            {isSubmitted ? (
              <div className="py-12 text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#f8efe5]">
                  <CheckCircle className="h-11 w-11 text-[#9d1f1c]" />
                </div>
                <h3 className="mt-6 text-2xl font-semibold text-[#14201f]">
                  {t("volunteer.form.submitted.title")}
                </h3>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-[#52615f]">
                  {t("volunteer.form.submitted.message")}
                </p>
                <button
                  type="button"
                  onClick={() => setIsSubmitted(false)}
                  className="mt-8 rounded-md bg-[#9d1f1c] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916]"
                >
                  {t("volunteer.form.submitted.registerAnother")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9d1f1c]">
                    {t("volunteer.form.profileFile.eyebrow")}
                  </p>
                  <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
                    <label
                      htmlFor="profile-upload"
                      className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-[#d7e2df] bg-[#f7faf9] px-4 py-3 text-sm font-semibold text-[#14201f] transition hover:border-[#9d1f1c]"
                    >
                      <Upload className="h-5 w-5 text-[#9d1f1c]" />
                      {t("volunteer.form.profileFile.uploadLabel")}
                      <input
                        id="profile-upload"
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>

                    {profileFile && (
                      <div className="flex min-w-0 items-center gap-3 rounded-md border border-[#cbe7df] bg-[#eefaf6] px-4 py-3">
                        <span className="truncate text-sm font-medium text-[#14201f]">
                          {profileFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="text-[#9d1f1c]"
                          aria-label={t("volunteer.form.profileFile.removeLabel")}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {profilePreview && (
                    <img
                      src={profilePreview}
                      alt={t("volunteer.form.profileFile.imageAlt")}
                      className="mt-4 h-28 w-28 rounded-md border border-[#dfe8e6] object-cover"
                    />
                  )}
                  <p className="mt-3 text-xs leading-5 text-[#60706d]">
                    {t("volunteer.form.profileFile.hint")}
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#14201f]">
                    {t("volunteer.form.section.personalInfo")}
                  </h3>
                  <div className="mt-5 grid gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.fullName")} *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                        <input
                          {...register("fullName")}
                          className={iconInputClass(errors.fullName)}
                          placeholder={t("volunteer.form.placeholders.fullName")}
                        />
                      </div>
                      <ErrorText error={errors.fullName} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.dob")} *
                      </label>
                      <div className="relative">
                        <CalendarDays className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                        <input
                          {...register("dob")}
                          type="date"
                          className={iconInputClass(errors.dob)}
                        />
                      </div>
                      <ErrorText error={errors.dob} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.email")} *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                        <input
                          {...register("email")}
                          type="email"
                          className={iconInputClass(errors.email)}
                          placeholder={t("volunteer.form.placeholders.email")}
                        />
                      </div>
                      <ErrorText error={errors.email} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.phone")} *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                        <input
                          {...register("phone")}
                          className={iconInputClass(errors.phone)}
                          placeholder={t("volunteer.form.placeholders.phone")}
                        />
                      </div>
                      <ErrorText error={errors.phone} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.age")} *
                      </label>
                      <input
                        {...register("age")}
                        type="number"
                        className={inputClass(errors.age)}
                        placeholder={t("volunteer.form.placeholders.age")}
                      />
                      <ErrorText error={errors.age} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.gender")} *
                      </label>
                      <select {...register("gender")} className={inputClass(errors.gender)}>
                        <option value="">{t("volunteer.form.placeholders.gender")}</option>
                        <option value="male">{t("volunteer.form.genders.male")}</option>
                        <option value="female">{t("volunteer.form.genders.female")}</option>
                        <option value="other">{t("volunteer.form.genders.other")}</option>
                        <option value="prefer_not_to_say">
                          {t("volunteer.form.genders.prefer_not_to_say")}
                        </option>
                      </select>
                      <ErrorText error={errors.gender} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.maritalStatus")} *
                      </label>
                      <select
                        {...register("maritalStatus")}
                        className={inputClass(errors.maritalStatus)}
                      >
                        <option value="">{t("volunteer.form.placeholders.maritalStatus")}</option>
                        <option value="single">{t("volunteer.form.maritalStatuses.single")}</option>
                        <option value="married">{t("volunteer.form.maritalStatuses.married")}</option>
                        <option value="divorced">
                          {t("volunteer.form.maritalStatuses.divorced")}
                        </option>
                        <option value="widowed">{t("volunteer.form.maritalStatuses.widowed")}</option>
                      </select>
                      <ErrorText error={errors.maritalStatus} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.isDikshit")} *
                      </label>
                      <label className="flex min-h-[46px] items-center justify-between rounded-md border border-[#d7e2df] px-4">
                        <span className="text-sm font-medium text-[#52615f]">
                          {watch("isDikshit")
                            ? t("volunteer.form.isDikshit.yes")
                            : t("volunteer.form.isDikshit.no")}
                        </span>
                        <input
                          type="checkbox"
                          {...register("isDikshit")}
                          className="h-5 w-5 rounded border-[#d7e2df] text-[#9d1f1c] focus:ring-[#9d1f1c]"
                        />
                      </label>
                      <ErrorText error={errors.isDikshit} />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#14201f]">
                    {t("volunteer.form.section.locationEducation")}
                  </h3>
                  <div className="mt-5 grid gap-5 md:grid-cols-2">
                    {[
                      [
                        "city",
                        t("volunteer.form.fields.city"),
                        t("volunteer.form.placeholders.city"),
                      ],
                      [
                        "state",
                        t("volunteer.form.fields.state"),
                        t("volunteer.form.placeholders.state"),
                      ],
                      [
                        "country",
                        t("volunteer.form.fields.country"),
                        t("volunteer.form.placeholders.country"),
                      ],
                      [
                        "zip",
                        t("volunteer.form.fields.zip"),
                        t("volunteer.form.placeholders.zip"),
                      ],
                    ].map(([name, label, placeholder]) => (
                      <div key={name}>
                        <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                          {label} *
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                          <input
                            {...register(name)}
                            className={iconInputClass(errors[name])}
                            placeholder={placeholder}
                          />
                        </div>
                        <ErrorText error={errors[name]} />
                      </div>
                    ))}

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.highestEducation")} *
                      </label>
                      <div className="relative">
                        <GraduationCap className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                        <input
                          {...register("highestEducation")}
                          className={iconInputClass(errors.highestEducation)}
                          placeholder={t("volunteer.form.placeholders.highestEducation")}
                        />
                      </div>
                      <ErrorText error={errors.highestEducation} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.occupationType")} *
                      </label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                        <select
                          {...register("occupationType")}
                          className={iconInputClass(errors.occupationType)}
                        >
                          <option value="">{t("volunteer.form.placeholders.occupationType")}</option>
                          <option value="private">
                            {t("volunteer.form.occupationTypes.private")}
                          </option>
                          <option value="government">
                            {t("volunteer.form.occupationTypes.government")}
                          </option>
                          <option value="business">
                            {t("volunteer.form.occupationTypes.business")}
                          </option>
                          <option value="unemployed">
                            {t("volunteer.form.occupationTypes.unemployed")}
                          </option>
                          <option value="student">
                            {t("volunteer.form.occupationTypes.student")}
                          </option>
                          <option value="retired">
                            {t("volunteer.form.occupationTypes.retired")}
                          </option>
                        </select>
                      </div>
                      <ErrorText error={errors.occupationType} />
                    </div>

                    {showOccupation && (
                      <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                          {t("volunteer.form.fields.occupation")} *
                        </label>
                        <div className="relative">
                          <Briefcase className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8b9996]" />
                          <input
                            {...register("occupation")}
                            className={iconInputClass(errors.occupation)}
                            placeholder={t("volunteer.form.placeholders.occupation")}
                          />
                        </div>
                        <ErrorText error={errors.occupation} />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#14201f]">
                    {t("volunteer.form.section.availabilitySkills")}
                  </h3>
                  <div className="mt-5 grid gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.hoursAvailable")} *
                      </label>
                      <div className="grid grid-cols-[1fr_132px] gap-2">
                        <input
                          {...register("hoursAvailable.hours")}
                          type="number"
                          className={inputClass(errors.hoursAvailable?.hours)}
                          placeholder={t("volunteer.form.placeholders.hours")}
                        />
                        <select
                          {...register("hoursAvailable.period")}
                          className={inputClass(errors.hoursAvailable?.period)}
                        >
                          <option value="">{t("volunteer.form.period.select")}</option>
                          <option value="day">{t("volunteer.form.period.day")}</option>
                          <option value="week">{t("volunteer.form.period.week")}</option>
                          <option value="month">{t("volunteer.form.period.month")}</option>
                        </select>
                      </div>
                      <ErrorText error={errors.hoursAvailable?.hours} />
                      <ErrorText error={errors.hoursAvailable?.period} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.availableFrom")} *
                      </label>
                      <input
                        {...register("availableFrom")}
                        type="date"
                        min={todayIso}
                        className={inputClass(errors.availableFrom)}
                      />
                      <ErrorText error={errors.availableFrom} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.availableUntil")}
                      </label>
                      <input
                        {...register("availableUntil")}
                        type="date"
                        min={todayIso}
                        className={inputClass(errors.availableUntil)}
                      />
                      <ErrorText error={errors.availableUntil} />
                    </div>
                  </div>

                  <div className="mt-6">
                    <p className="mb-3 text-sm font-semibold text-[#14201f]">
                      {t("volunteer.form.fields.availability")} *
                    </p>
                    <div className="grid gap-3 md:grid-cols-2">
                      {availabilityOptions.map((option) => (
                        <label
                          key={option.id}
                          className="flex items-center gap-3 rounded-md border border-[#d7e2df] px-4 py-3 text-sm font-medium text-[#52615f]"
                        >
                          <input
                            {...register("availability")}
                            type="checkbox"
                            value={option.id}
                            className="h-4 w-4 rounded border-[#d7e2df] text-[#9d1f1c] focus:ring-[#9d1f1c]"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                    <ErrorText error={errors.availability} />
                  </div>

                  <div className="mt-6">
                    <p className="mb-3 text-sm font-semibold text-[#14201f]">
                      {t("volunteer.form.fields.skills")} *
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {skillOptions.map((skill) => (
                        <label
                          key={skill.id}
                          className="flex items-center gap-3 rounded-md border border-[#d7e2df] px-4 py-3 text-sm font-medium text-[#52615f]"
                        >
                          <input
                            {...register("skills")}
                            type="checkbox"
                            value={skill.id}
                            className="h-4 w-4 rounded border-[#d7e2df] text-[#9d1f1c] focus:ring-[#9d1f1c]"
                          />
                          {skill.label}
                        </label>
                      ))}
                    </div>
                    <ErrorText error={errors.skills} />
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#14201f]">
                    {t("volunteer.form.section.motivation")}
                  </h3>
                  <div className="mt-5 space-y-5">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.motivation")} *
                      </label>
                      <textarea
                        {...register("motivation")}
                        rows={5}
                        className={inputClass(errors.motivation)}
                        placeholder={t("volunteer.form.placeholders.motivation")}
                      />
                      <ErrorText error={errors.motivation} />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#14201f]">
                        {t("volunteer.form.fields.experience")}
                      </label>
                      <textarea
                        {...register("experience")}
                        rows={4}
                        className={inputClass(errors.experience)}
                        placeholder={t("volunteer.form.placeholders.experience")}
                      />
                      <ErrorText error={errors.experience} />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="flex items-start gap-3 rounded-md border border-[#d7e2df] bg-[#f7faf9] p-4">
                    <input
                      {...register("consent")}
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-[#d7e2df] text-[#9d1f1c] focus:ring-[#9d1f1c]"
                    />
                    <span className="text-sm leading-6 text-[#52615f]">
                      {t("volunteer.form.fields.consent")}
                    </span>
                  </label>
                  <ErrorText error={errors.consent} />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-6 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#7f1916] disabled:cursor-not-allowed disabled:opacity-65 sm:w-auto"
                >
                  {isSubmitting
                    ? t("volunteer.form.processing")
                    : t("volunteer.form.submitButton")}
                  <ArrowRight size={18} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="bg-[#14201f] py-14 text-white">
        <div className="container mx-auto grid gap-6 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
          {[
            [Heart, t("volunteer.ethos.seva.title"), t("volunteer.ethos.seva.text")],
            [Users, t("volunteer.ethos.team.title"), t("volunteer.ethos.team.text")],
            [HandHeart, t("volunteer.ethos.dignity.title"), t("volunteer.ethos.dignity.text")],
          ].map(([Icon, title, text]) => (
            <div key={title} className="rounded-lg border border-white/14 bg-white/8 p-5">
              <Icon className="h-6 w-6 text-[#f5c45e]" />
              <h2 className="mt-4 text-lg font-semibold">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-white/72">{text}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Volunteer;
